import React, { useState, useEffect } from 'react';
import { Edit3, Check, Store, Mail, Award, Clock } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


function Section({ title, icon, children }) {
  return (
    <div className="bg-[#12121A] border border-white/5 rounded-2xl p-5 space-y-4">
      <h3 className="text-[#C9A962] font-semibold text-sm flex items-center gap-2 uppercase tracking-wider">
        {React.cloneElement(icon, { size: 14 })}{title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, span2 = false }) {
  return (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <label className="block text-[#A1A1AA] text-xs font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function AdminSettings({ storeSettings, lang, setMessage }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const l = createTextPicker(lang);

  useEffect(() => {
    if (storeSettings) setForm(storeSettings);
  }, [storeSettings]);

  const inputClass = "w-full bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#C9A962]";

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/settings`, form);
      setMessage(l('Configuración guardada', 'Settings saved'));
      setEditing(false);
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const s = storeSettings || {};

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-white font-bold text-xl">{l('Configuración', 'Settings')}</h2>
        <button onClick={() => setEditing(e => !e)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
          <Edit3 size={15} />{editing ? l('Cancelar', 'Cancel') : l('Editar', 'Edit')}
        </button>
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="space-y-5">

          <Section title={l('Tienda', 'Store')} icon={<Store />}>
            <Field label={l('Nombre de la tienda', 'Store name')}>
              <input value={form.store_name || ''} onChange={e => setForm(f => ({ ...f, store_name: e.target.value }))} placeholder="El Rincón de Camo" className={inputClass} />
            </Field>
            <Field label="Website">
              <input value={form.website || ''} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="www.elrincondecamo.com" className={inputClass} />
            </Field>
            <Field label="Email de contacto">
              <input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="info@..." className={inputClass} />
            </Field>
            <Field label={l('Teléfono', 'Phone')}>
              <input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+34 000 000 000" className={inputClass} />
            </Field>
            <Field label={l('Dirección', 'Address')} span2>
              <input value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Calle Ejemplo 1" className={inputClass} />
            </Field>
            <Field label={l('Horario', 'Opening hours')}>
              <input value={form.opening_hours || ''} onChange={e => setForm(f => ({ ...f, opening_hours: e.target.value }))} placeholder="10:00 - 22:00" className={inputClass} />
            </Field>
            <Field label={l('Días de apertura', 'Opening days')}>
              <input value={form.opening_days || ''} onChange={e => setForm(f => ({ ...f, opening_days: e.target.value }))} placeholder="Lun-Dom" className={inputClass} />
            </Field>
          </Section>

          <Section title={l('Email y Notificaciones', 'Email & Notifications')} icon={<Mail />}>
            <Field label={l('Email remitente (SENDER)', 'Sender email')} span2>
              <input type="email" value={form.sender_email || ''} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))}
                placeholder="administracion@elrincondecamo.com"
                className={inputClass} />
              <p className="text-[#52525B] text-[10px] mt-1">{l('Email desde el que se envían los correos automáticos.', 'Email used to send automatic notifications.')}</p>
            </Field>
          </Section>

          <Section title={l('Programa de Fidelidad', 'Loyalty Program')} icon={<Award />}>
            <Field label={l('Puntos por euro gastado', 'Points per euro spent')}>
              <input type="number" min="1" value={form.points_per_euro ?? 10} onChange={e => setForm(f => ({ ...f, points_per_euro: parseInt(e.target.value) || 10 }))} className={inputClass} />
              <p className="text-[#52525B] text-[10px] mt-1">{l('Ej: 10 = el usuario gana 10 puntos por cada euro.', 'E.g. 10 = user earns 10 pts per euro.')}</p>
            </Field>
            <Field label={l('Puntos por reserva de juego', 'Points per game reservation')}>
              <input type="number" min="0" value={form.points_per_reservation ?? 50} onChange={e => setForm(f => ({ ...f, points_per_reservation: parseInt(e.target.value) || 0 }))} className={inputClass} />
            </Field>
          </Section>

          <Section title={l('Política de Reservas', 'Reservation Policy')} icon={<Clock />}>
            <Field label={l('Máx. días de antelación', 'Max days in advance')}>
              <input type="number" min="1" max="365" value={form.max_reservation_days_ahead ?? 30} onChange={e => setForm(f => ({ ...f, max_reservation_days_ahead: parseInt(e.target.value) || 30 }))} className={inputClass} />
            </Field>
            <Field label={l('Cancelación libre (horas antes)', 'Free cancellation (hours before)')}>
              <input type="number" min="0" value={form.reservation_cancellation_hours ?? 24} onChange={e => setForm(f => ({ ...f, reservation_cancellation_hours: parseInt(e.target.value) || 0 }))} className={inputClass} />
            </Field>
          </Section>

          <button type="submit" className="w-full btn-primary py-3 text-sm flex items-center justify-center gap-2">
            <Check size={16} />{l('Guardar todos los ajustes', 'Save all settings')}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <Section title={l('Tienda', 'Store')} icon={<Store />}>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Nombre', 'Name')}</p><p className="text-white text-sm">{s.store_name || '-'}</p></div>
            <div><p className="text-[#52525B] text-xs mb-0.5">Email</p><p className="text-white text-sm">{s.email || '-'}</p></div>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Teléfono', 'Phone')}</p><p className="text-white text-sm">{s.phone || '-'}</p></div>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Horario', 'Hours')}</p><p className="text-white text-sm">{s.opening_hours || '-'} ({s.opening_days || '-'})</p></div>
            <div className="sm:col-span-2"><p className="text-[#52525B] text-xs mb-0.5">{l('Dirección', 'Address')}</p><p className="text-white text-sm">{s.address || '-'}</p></div>
          </Section>
          <Section title={l('Fidelidad', 'Loyalty')} icon={<Award />}>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Pts/euro', 'Pts/euro')}</p><p className="text-white text-sm">{s.points_per_euro ?? 10}</p></div>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Pts/reserva', 'Pts/reservation')}</p><p className="text-white text-sm">{s.points_per_reservation ?? 50}</p></div>
            <div><p className="text-[#52525B] text-xs mb-0.5">{l('Cancelación', 'Cancellation')}</p><p className="text-white text-sm">{s.reservation_cancellation_hours ?? 24}h</p></div>
          </Section>
        </div>
      )}
    </div>
  );
}
